import React, { FC, useEffect, useRef, useState } from 'react';
import { AchInfoFormState } from '../../ach-info/ach-info/ach-info';
import { CardInfoFormState } from '../../card-info/card-info/card-info';
import { PGCardInfoFormState } from '../../card-info/pg-card-info/pg-card-info';
import { PaymentAmountFormState } from '../../payment-amount/payment-amount';
import { AchInfoContainer } from '../../ach-info/ach-info';

import styles from './payment-methods.less';
import classnames from 'classnames';
import { Radio } from '@material-ui/core';

import {
    usePlaidLink,
    PlaidLinkOptions,
    PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import { ConnectedBankAccount, PaymentMethodType, PaymentMethodInfo } from 'src/invoice/types/types';

import { ConnectedAccounts } from '../../connected-accounts/connected-accounts';
import { Checkbox, IconNameType } from '@servicetitan/design-system';
import { CardInfo } from '../../card-info/card-info/desktop/card-info';
import { PGCardInfo } from '../../card-info/pg-card-info/desktop/pg-card-info';
import { paymentMethodsContainer } from 'src/payment-method/ioc';
import { PaymentGateway } from 'src/statement/stores/payment-gateway.store';
import { PendingInvoicePayment, SavedPaymentMethodsStore } from 'src/payment-method/stores/saved-payment-methods.store';
import { ChargeType, SplitInfo } from 'src/api';
import { ConfirmationModal } from 'src/payment/components/ach/desktop/confirmation-modal';
import { ClientDataStore } from 'src/core/stores/client-data.store';

interface PaymentMethodProps<Params extends { [K in keyof Params]?: string } = {}> {
    paymentAmountData?: PaymentAmountFormState | undefined;
    cardInfoData: CardInfoFormState;
    pgcardInfoData: PGCardInfoFormState;
    achInfoData: AchInfoFormState;
    usePaymentGateway: () => boolean;
    showLoginLink: boolean;
    setPaymentMethodType?: ((paymentMethodType: PaymentMethodType) => void) | undefined;
    setPaymentMethodValid: (isValid: boolean) => void;
    setPaymentMethodAction: (action: () => void) => void;
    setPaymentMethodSubmit?: (submit: () => void) => void;
    setConnectedAccounts: (accounts: ConnectedBankAccount[]) => void;
    setPaymentButtonTextIcon?: (text: string, icon?: IconNameType | undefined) => void;
    showPrimaryOption?: boolean;
    showSavePaymentOption?: boolean;
    savePaymentChecked?: boolean;
    setSavePaymentChecked?: (checked: boolean) => void;
    useAsPrimaryPaymentChecked?: boolean;
    setUseAsPrimaryPaymentChecked?: (checked: boolean) => void;
    invoiceId?: string | undefined;
    statementId?: string;
    splits?: SplitInfo[];
    paymentMethodStore?: SavedPaymentMethodsStore;
    chargeType: ChargeType;
    setPayByBankReady?: (ready: boolean) => void;
    useFeatureGate?: boolean;
    showCardholderAgreement?: boolean;
};

interface PlaidLinkViewProps {
    linkToken: string | null;
    processPlaidSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => void;
    setFailedBankName: (name: string | undefined) => void;
    setReady: (ready: boolean) => void;
    setOpen: (calback: Function) => void;
}

const PlaidLinkView: FC<PlaidLinkViewProps> = ({
    linkToken,
    processPlaidSuccess,
    setFailedBankName,
    setReady,
    setOpen
}) => {

    const config: PlaidLinkOptions = {
        onSuccess: (publicToken, metadata) => {
            processPlaidSuccess(publicToken, metadata);
        },
        onExit: (err, metadata) => {
            // Plaid invokes onExit for both real errors and user-initiated cancellations.
            // Treat cancellations/closures (no error) as non-errors to avoid showing a scary banner.
            if (!err) {
                setFailedBankName(undefined);
                return;
            }

            setFailedBankName(metadata.institution?.name);
        },
        onEvent: () => {},
        token: linkToken,
    };

    const { open, ready } = usePlaidLink(config);

    useEffect(() => {
        setOpen(open);
        setReady(ready);
    }, [ready, open]);

    return (
        <></>
    );
}

export const PaymentMethods: FC<PaymentMethodProps> = ({
    paymentAmountData,
    cardInfoData,
    pgcardInfoData,
    achInfoData,
    usePaymentGateway,
    showLoginLink,
    setPaymentMethodValid,
    setPaymentMethodAction,
    setConnectedAccounts,
    setPaymentButtonTextIcon,
    setPaymentMethodType,
    showPrimaryOption,
    showSavePaymentOption,
    savePaymentChecked,
    useAsPrimaryPaymentChecked,
    setSavePaymentChecked,
    setUseAsPrimaryPaymentChecked,
    invoiceId,
    statementId,
    paymentMethodStore,
    setPaymentMethodSubmit,
    splits,
    chargeType,
    setPayByBankReady,
    useFeatureGate,
    showCardholderAgreement = true
}) => {

    const [isCreditCard, setIsCreditCard] = useState(false);
    const [isAch, setIsAch] = useState(false);
    const [isPayByBank, setIsPayByBank] = useState(false);
    const accountsRef = useRef<ConnectedBankAccount[]>([]);
    const [accounts, setAccounts] = useState<ConnectedBankAccount[]>([]);
    const [failedBankName, setFailedBankName] = useState<string | undefined>(undefined);
    const store = paymentMethodStore || paymentMethodsContainer.get(SavedPaymentMethodsStore);
    const clientDataStore = paymentMethodsContainer.get(ClientDataStore);
    const paymentMethodTypeRef = useRef(PaymentMethodType.PayByBank);
    const paymentMethodLoadedRef = useRef(false);
    const [isACHModelOpen, setIsACHModelOpen] = useState(false);
    const [linkToken, setLinkToken] = useState<string | undefined>(undefined);
    const [ready, setReady] = useState(false);
    const open = useRef<Function>(() => { });
    const [, setIsAchExceeded] = useState<boolean | null>(null);
    const isSavedPaymentChecked = useRef(savePaymentChecked);
    const isUseAsPrimaryPaymentChecked = useRef(useAsPrimaryPaymentChecked);

    const paymentGateway = paymentMethodsContainer.get(PaymentGateway);
    const iframeMessageHandler = paymentGateway.iframeMessageHandler;

    useEffect(() => {
        if(!clientDataStore.isInitialized) clientDataStore.init();
    }, [clientDataStore.init]);

    useEffect(() => {
        window.addEventListener('message', iframeMessageHandler);
        return () => {
            window.removeEventListener('message', iframeMessageHandler);
        };
    }, []);

    const setOpen = (callback: Function) => {
        open.current = callback;
    }

    const fetchLinkToken = async () => {
        // Only set token once
        if (linkToken) {
            return;
        }

        const token = await store.getLinkToken();
        // Update link token
        setLinkToken(token);
    };

    useEffect(() => {
        store.currentInvoiceId = invoiceId;
        store.currentSplits = splits;
        store.currentStatementId = statementId;
        store.init(() => {
            fetchLinkToken();
            if ((!useFeatureGate && store.isPayByBankEnabled) || (useFeatureGate && store.isPayByBankFeatureOn)) {
                onPayByBankSelected();
            } else if ((!useFeatureGate && store.isCreditCardEnabled) || (useFeatureGate && store.isSavedCreditCardEnabled)) {
                onCreditCardSelected();
            } else if ((!useFeatureGate && store.isBankAccountEnabled) || (useFeatureGate && store.isSavedBankAccountFeatureOn)) {
                onACHSelected();
            }
        });
    }, [store.init]);


    const processPlaidSuccess = (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
        const connectedAccounts = metadata.accounts;
        const institution = metadata.institution;
        const institutionId = institution?.institution_id || '';
        if (!connectedAccounts) {
            return;
        }

        store.getInstitution(institutionId).then((institutionLogo) => {
            const updatedAccounts = accountsRef.current.filter(account => account.bankId !== institutionId);
            connectedAccounts.forEach(account => {
                // TODO: Switch to account.verification_status
                const verificationStatus = 'AutomaticallyVerified';
                console.log(account.verification_status);

                updatedAccounts.push({
                    bankIcon: institutionLogo || 'assets/images/ach-account.svg',
                    bankName: institution?.name,
                    accountName: account.name,
                    accountMask: account.mask,
                    verificationStatus,
                    selected: false,
                    bankId: institutionId,
                    accountType: account.subtype,
                    accountId: account.id,
                    publicToken
                });
            });

            const selectedAccount = updatedAccounts.find(account => account.selected);

            if (!selectedAccount && updatedAccounts.length > 0) {
                // Default first connected account to selected if previous account was replaced
                updatedAccounts[0].selected = true;
            }

            updateConnectedAccounts(updatedAccounts);
        });
    }

    const onPayByBankAction = () => {
        if (accountsRef.current.length > 0) {
            return;
        }

        if (ready) {
            setFailedBankName(undefined);
            connectAnotherAccount();
        }
    };

    const onCreditCardAction = async () => {
        usePaymentGateway()
            ? await pgcardInfoData.form.validate()
            : await cardInfoData.form.validate();
    };

    const onACHAction = async () => {
        await achInfoData.form.validate();
    };

    const getPaymentAmount = (): number => {
        if (!paymentAmountData) {
            return 0;
        }

        return (!paymentAmountData.isFullBalance.value
            ? parseFloat(paymentAmountData.customAmount.value)
            : paymentAmountData.fullBalance) || 0;
    };

    const resetSelectedStates = () => {
        setIsAch(false);
        setIsCreditCard(false);
        setIsPayByBank(false);
    }

    const onCreditCardSelected = () => {
        resetSelectedStates();
        setIsCreditCard(true);
        updatePaymentMethodType(PaymentMethodType.CreditCard);
        setButtonTextIcon('Pay');
        setPaymentMethodValid(true);
        setPaymentMethodAction(onCreditCardAction);
    };

    const onPayByBankSelected = () => {
        resetSelectedStates();
        setIsPayByBank(true);
        updatePaymentMethodType(PaymentMethodType.PayByBank);
        updateConnectedAccounts(accounts);
        setPaymentMethodValid(true);
        setPaymentMethodAction(onPayByBankAction);
    };

    const updatePaymentMethodType = (paymentMethodType: PaymentMethodType) => {
        if (setPaymentMethodType) {
            setPaymentMethodType(paymentMethodType);
        }
        paymentMethodTypeRef.current = paymentMethodType;
    }

    const setPaymentButtonForPayByBank = () => {
        let icon: IconNameType | undefined = 'lock';
        let text = 'Connect Bank Account';
        if (accountsRef.current.length > 0) {
            const selectedAccount = accountsRef.current.filter(acct => acct.selected)[0];
            icon = undefined;
            text = `Pay with ${selectedAccount.accountName} Ending *${selectedAccount.accountMask}`;
        }

        setButtonTextIcon(text, icon);
    };

    const onACHSelected = () => {
        resetSelectedStates();
        setIsAch(true);
        updatePaymentMethodType(PaymentMethodType.ACH);
        setButtonTextIcon('Pay');
        setPaymentMethodValid(true);
        setPaymentMethodAction(onACHAction);
    };

    const setButtonTextIcon = (text: string, icon?: IconNameType | undefined) => {
        if (setPaymentButtonTextIcon) {
            setPaymentButtonTextIcon(text, icon);
        }
    };

    const updateConnectedAccounts = (connectedAccounts: ConnectedBankAccount[]) => {
        setAccounts(connectedAccounts);
        setConnectedAccounts(connectedAccounts);
        accountsRef.current = connectedAccounts;
        setPaymentButtonForPayByBank();
    };

    const connectAnotherAccount = () => {
        setFailedBankName(undefined);
        open.current();
    };

    const onConfirmACHPaymentMethod = async () => {
        const amount = getPaymentAmount();
        const payment: PendingInvoicePayment = {
            amount,
            invoiceNumber: invoiceId!,
            amountRemaining: paymentAmountData?.fullBalance! - amount,
            chargeType,
            splits,
            statementId
        };
        const achPaymentMethod = await store.achInfoToPaymentMethod(achInfoData, false);
        if (!achPaymentMethod) {
            return;
        }

        setIsACHModelOpen(false);

        if (showPrimaryOption && isUseAsPrimaryPaymentChecked.current) {
            store.needToStoreAsPrimary = true;
            achPaymentMethod.primary = true;
        }

        if (showSavePaymentOption && isSavedPaymentChecked.current) {
            await store.addPaymentMethod(achPaymentMethod!);
        }

        await store.chargePaymentMethod(achPaymentMethod, payment);

    }

    const onPaymentMethodSubmit = async () => {
        const amount = getPaymentAmount();
        const type = paymentMethodTypeRef.current;

        const payment: PendingInvoicePayment = {
            amount,
            invoiceNumber: invoiceId!,
            amountRemaining: paymentAmountData?.fullBalance! - amount,
            chargeType,
            splits,
            statementId
        };

        if (type === PaymentMethodType.ACH) {
            const achPaymentMethod = await store.achInfoToPaymentMethod(achInfoData, false);
            if (!achPaymentMethod) {
                return;
            }

            setIsACHModelOpen(true);
        } else if (type === PaymentMethodType.CreditCard) {
            if (usePaymentGateway()) {
                const result = await pgcardInfoData.form.validate();

                if (result.hasError) {
                    return;
                }

                const paymentMethod = { paymentMethodId: 0 } as PaymentMethodInfo;
                paymentMethod.paymentMethodType = PaymentMethodType.CreditCard;

                await store.processGatewayAddNewCard(async (gatewayPaymentMethod) => {
                    if (gatewayPaymentMethod) {
                        if (showPrimaryOption && isUseAsPrimaryPaymentChecked.current && isSavedPaymentChecked.current) {
                            gatewayPaymentMethod!.primary = true;
                            store.needToStoreAsPrimary = true;
                        }

                        if (showSavePaymentOption && isSavedPaymentChecked.current) {
                            gatewayPaymentMethod!.storePaymentMethod = true;
                        }
                        store.chargePaymentMethod(gatewayPaymentMethod!, payment);
                    }
                });

            } else {
                const creditCardPaymentMethod = await store.cardInfoToPaymentMethod(cardInfoData, false);
                if (!creditCardPaymentMethod) {
                    return;
                }

                if (showPrimaryOption && isUseAsPrimaryPaymentChecked.current) {
                    creditCardPaymentMethod.primary = true;
                    store.needToStoreAsPrimary = true;
                }

                if (showSavePaymentOption && isSavedPaymentChecked.current) {
                    creditCardPaymentMethod.storePaymentMethod = true;
                }

                await store.chargePaymentMethod(creditCardPaymentMethod, payment);
            }
        } else if (type === PaymentMethodType.PayByBank) {
            if (accountsRef.current.length < 1) {
                return;
            }

            const connectedAccount = accountsRef.current.filter(account => account.selected)[0];

            const payByBankPaymentMethod = store.connectedBankAccountToPaymentMethod(connectedAccount,
                false);

            if (showPrimaryOption && isUseAsPrimaryPaymentChecked.current) {
                payByBankPaymentMethod.primary = true;
                store.needToStoreAsPrimary = true;
            }

            if (showSavePaymentOption && isSavedPaymentChecked.current) {
                await store.addPaymentMethod(payByBankPaymentMethod!);
            }

            await store.chargePaymentMethod(payByBankPaymentMethod, payment);
        }
    };

    const onAmountStateChanged = () => {
        // Use for triggering refresh for amount change with regards to ACH amount
        setIsAchExceeded(isAchAmountExceeded);
    };

    const isAchAmountExceeded = () => {
        const maxAmount = 250000;
        return paymentMethodTypeRef.current === PaymentMethodType.ACH && getPaymentAmount() > maxAmount;
    }

    useEffect(() => {
        store.postalCode = store.usePaymentGateway() ? pgcardInfoData.postalCode : cardInfoData.postalCode;

        if (paymentAmountData) {
            paymentAmountData.onStateChanged = onAmountStateChanged;
        }

        if (!paymentMethodLoadedRef.current) {
            paymentMethodLoadedRef.current = true;

            if (setPaymentMethodSubmit) {
                setPaymentMethodSubmit(onPaymentMethodSubmit);
            }

            if (isCreditCard) {
                onCreditCardSelected()
            }

            if (isAch) {
                onACHSelected();
            }

            if (isPayByBank) {
                onPayByBankSelected();
            }

            pgcardInfoData.desktopPaymentGatewayUrl = paymentGateway.getDesktopPaymentGatewayUrl(store.usesPaymentsService);
            pgcardInfoData.mobilePaymentGatewayUrl = paymentGateway.getMobilePaymentGatewayUrl(store.usesPaymentsService);
            pgcardInfoData.iframeMessageHandler =
                pgcardInfoData.iframeMessageHandler || iframeMessageHandler;
        }
    }, []);

    useEffect(() => {
        if (ready && paymentMethodTypeRef.current === PaymentMethodType.PayByBank) {
            setPaymentMethodAction(onPayByBankAction);
        }
        if (ready && setPayByBankReady) {
            setPayByBankReady(ready);
        }
    }, [ready, open]);

    return (
        <>
            {linkToken && (<PlaidLinkView
                linkToken={linkToken}
                setFailedBankName={setFailedBankName}
                processPlaidSuccess={processPlaidSuccess}
                setReady={setReady}
                setOpen={setOpen}
            />)}
            {((!useFeatureGate && store.isCreditCardEnabled) || (useFeatureGate && store.isSavedCreditCardEnabled)) && <label
                className={classnames('radioButtonLabel', { checked: isCreditCard })}
            >
                <div className={classnames('payment-methods-container', styles.paymentMethodsContainer)}>
                    <Radio checked={isCreditCard} onChange={() => onCreditCardSelected()} />
                    <strong>Credit Card</strong>
                    <div className={classnames('payment-methods-spacing', styles.paymentMethodsSpacing)}>
                        {isCreditCard && (usePaymentGateway() ? (
                            pgcardInfoData ? (
                                <PGCardInfo
                                    pgdata={pgcardInfoData}
                                    isEmbedded
                                    showSavePaymentInformation={false}
                                    showCardholderAgreement={!!showCardholderAgreement} />
                            ) : null
                        ) : cardInfoData ? (
                            <CardInfo
                                data={cardInfoData}
                                showCardholderAgreement={!!showCardholderAgreement} />
                        ) : null)}
                    </div>
                </div>

            </label>}

            {((!useFeatureGate && store.isPayByBankEnabled) || (useFeatureGate && store.isPayByBankFeatureOn)) && <label
                className={classnames('radioButtonLabel', { checked: isPayByBank })}
            >
                <div className={classnames('payment-methods-container', styles.paymentMethodsContainer)}>
                    <Radio checked={isPayByBank} onChange={() => onPayByBankSelected()} />
                    <strong>Pay By Bank</strong>
                    <div className={classnames('payment-methods-spacing', styles.paymentMethodsSpacing)}>
                        <span>
                            Securely connect to your bank for faster payments and easier refund.
                        </span>
                        {isPayByBank && (
                            <>
                                <ConnectedAccounts
                                    accounts={accounts}
                                    setAccounts={updateConnectedAccounts}
                                />
                                {accounts.length > 0 && (
                                    <span className={classnames('payment-methods-connect-bank', styles.paymentMethodsConnectBank)}>
                                        <a onClick={connectAnotherAccount}>{accounts.length > 1 ? 'Connect a Different Account' : 'Connect Another Account'}</a>
                                    </span>
                                )}

                                {failedBankName && (
                                    <div className={classnames('payment-methods-pay-by-bank-error', styles.paymentMethodsPayByBankError)}>
                                        <div className={classnames('pay-by-bank-error-header', styles.payByBankErrorHeader)}>
                                            <img className={classnames('pay-by-bank-error-warning', styles.payByBankErrorWarning)}
                                                src="/assets/images/warning-exclamation.svg" />
                                            <span className={classnames('pay-by-bank-error-header-title', styles.payByBankErrorHeaderTitle)}>
                                                Plaid had an issue connecting to your bank ({failedBankName})
                                            </span>
                                        </div>
                                        <div className={classnames('pay-by-bank-error-body', styles.payByBankErrorBody)}>
                                            You can try connecting to the bank again later, try connecting to another bank, or use a different payment method.
                                        </div>
                                    </div>)}
                            </>
                        )}
                    </div>
                </div>
            </label>}

            {((!useFeatureGate && store.isBankAccountEnabled) || (useFeatureGate && store.isSavedBankAccountFeatureOn)) && (<label
                className={classnames('radioButtonLabel', { checked: isAch })}
            >
                <div className={classnames('payment-methods-container', styles.paymentMethodsContainer)}>
                    <Radio checked={isAch} onChange={() => onACHSelected()} />
                    <strong>ACH</strong>
                    <div className={classnames('payment-methods-spacing', styles.paymentMethodsSpacing)}>
                        {(isAch && <AchInfoContainer
                            data={achInfoData}
                            isInvoicePayment
                            amount={getPaymentAmount()}
                            isEmbedded
                        />
                        )}
                    </div>
                </div>
            </label>)}

            {showSavePaymentOption && (
                <div className={styles.paymentMethodsCheckboxItem}>
                    <Checkbox checked={savePaymentChecked} className={styles.paymentMethodsCheckbox} onChange={(_value, checked) => {
                        isSavedPaymentChecked.current = checked;
                        if (setSavePaymentChecked) {
                            setSavePaymentChecked(checked)
                        }
                    }} />
                    <span>Save Payment Information</span>
                </div>
            )}

            {showPrimaryOption && (
                <div className={styles.paymentMethodsCheckboxItem}>
                    <Checkbox checked={useAsPrimaryPaymentChecked} disabled={!savePaymentChecked} className={styles.paymentMethodsCheckbox}
                        onChange={(_value, checked) => {
                            isUseAsPrimaryPaymentChecked.current = checked;
                            if (setUseAsPrimaryPaymentChecked) {
                                setUseAsPrimaryPaymentChecked!(checked);
                            }
                        }} />
                    <span>Use as Primary Payment Method</span>
                </div>
            )}

            {showLoginLink && (
                <span className={classnames('sign-in-message', styles.signInMessage)}><a href={clientDataStore.createTenantURL('/login')}>Sign in</a> to access your saved payment information.</span>)}

            {isACHModelOpen && (
                <ConfirmationModal
                    amount={getPaymentAmount()}
                    companyName={achInfoData.companyName || ''}
                    companyPhoneNumber={achInfoData.phone || ''}
                    onClose={() => setIsACHModelOpen(false)}
                    onSubmit={onConfirmACHPaymentMethod}
                    accountNumber={achInfoData.accountNumber.value || ''}
                />
            )}
        </>
    )
};
